import '../../assets/styles/auth.scss';
import '../../assets/styles/pages.scss';
import { Fragment, useContext, useEffect, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { GlobalContext, handleError } from '../../context/Provider';
import henceforthApi, { GOOGLE_API_KEY } from '../../utils/henceforthApi';
import { toast } from 'react-toastify';
import { validateEmail } from '../../utils/validations';
import Inputs, { SaveCancelButton } from '../../components/common/Inputs';
import Textarea from '../../components/common/Textarea';
import PhoneInput from 'react-phone-input-2';
import Errormessage from '../../components/common/errormessage';
import Svg from '../../components/common/error-svg';
import BreadCrumb from '../../components/common/BreadCrumb';
import { isValidPhoneNumber } from 'libphonenumber-js';
import { NumberValidation } from '../../utils/validations';

interface StoreData {
    _id: string;
    name: string;
    email: string;
    country_code: string;
    phone_number: number;
    image: string | null;
    company: string;
    country: string;
    state: string;
    city: string;
    pin_code: string;
    apartment_number: string;
    full_address: string;
    location: {
        type: string;
        coordinates: [number, number];
    };
    account_status: 'ACTIVATED' | 'DEACTIVATED' | 'SUSPENDED';
    is_admin_verified: 'approve' | 'pending' | 'reject';
    documents: any[];
    language: string;
    role: string;
    master_seller_id: string;
    delivery_provide: boolean;
    admin_commission: number | null;
    rejection_reason?: string;
    is_blocked: boolean;
    is_deleted: boolean;
    created_at: number;
    updated_at: number;
}

const EditStoreProfile = () => {
    const { language_data, authState, loading, setLoading, toastMessage, authDispatch } = useContext(GlobalContext);
    const navigate = useNavigate();
    const { id } = useParams<{ id: string }>();
    const placeInputRef = useRef(null) as React.MutableRefObject<any>;

    const [storeData, setStoreData] = useState<StoreData | null>(null);
    const [formData, setFormData] = useState({
        name: '',
        country_code: '220',
        phone_number: '',
        full_address: '',
        city: '',
        state: '',
        pin_code: '',
        apartment_number: '',
        latitude: '',
        longitude: '',
        account_status: 'ACTIVATED' as 'ACTIVATED' | 'DEACTIVATED',
    });

    // Validation errors
    const [nameErr, setNameErr] = useState('');
    const [phoneErr, setPhoneErr] = useState('');
    const [fullAddressErr, setFullAddressErr] = useState('');
    const [cityErr, setCityErr] = useState('');
    const [stateErr, setStateErr] = useState('');
    const [pinCodeErr, setPinCodeErr] = useState('');
    const [apartmentNoErr, setApartmentNoErr] = useState('');
    const [countryIsoCode, setCountryIsoCode] = useState('gm');

    const [showDeleteModal, setShowDeleteModal] = useState(false);
    const [deleteLoading, setDeleteLoading] = useState(false);

    // Breadcrumb
    const breadCrumbPath = [
        { name: 'Home', url: '/', active: '' },
        { name: 'Store', url: '/stores', active: '' },
        { name: 'Store Profile', url: `/store/${id}`, active: '' },
        { name: 'Edit Store', url: '', active: 'not-allowed' },
    ];

    // Fetch store details
    const fetchStoreDetail = async () => {
        if (!id) {
            toast.error('Store ID is required');
            navigate('/stores');
            return;
        }

        setLoading(true);
        try {
            const response = await henceforthApi.Store.getStoreDetail(id);
            if (response?.data && Array.isArray(response.data) && response.data.length > 0) {
                const store = response.data[0];
                setStoreData(store);
                setFormData({
                    name: store.name || '',
                    country_code: store.country_code || '',
                    phone_number: store.phone_number?.toString() || '',
                    full_address: store.full_address || '',
                    city: store.city || '',
                    state: store.state || '',
                    pin_code: store.pin_code || '',
                    apartment_number: store.apartment_number || '',
                    latitude: store.location?.coordinates?.[1]?.toString() || '',
                    longitude: store.location?.coordinates?.[0]?.toString() || '',
                    account_status: store.account_status || 'ACTIVATED',
                });
            } else {
                toast.error('Store not found');
                navigate('/stores');
            }
        } catch (error) {
            // handleError(error, 'active', authDispatch);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchStoreDetail();
    }, [id]);

    // Phone change handler
    const phoneCheck = (e: any, v: any) => {
        setPhoneErr('');
        setCountryIsoCode(v.countryCode || '');
        setFormData({
            ...formData,
            phone_number: e.phone.slice(Number(v.dialCode.length), Number(e.phone.length)),
            country_code: v.dialCode,
        });
    };

    // Form field change
    const changeFormData = ({ target }: any) => {
        const val = target.value;
        const name = target.name;
        if (name === 'name') setNameErr('');
        if (name === 'full_address') setFullAddressErr('');
        if (name === 'city') setCityErr('');
        if (name === 'state') setStateErr('');
        if (name === 'pin_code' && !NumberValidation(val)) return;
        if (name === 'apartment_number') setApartmentNoErr('');

        setFormData({ ...formData, [name]: val });
    };

    // Google Places autocomplete
    const initPlaceAPI = () => {
        const loadGoogleMapScript = (callback: any) => {
            if (typeof (window as any).google === 'object' && typeof (window as any).google.maps === 'object') {
                callback();
            } else {
                const script = document.createElement('script');
                script.src = `https://maps.googleapis.com/maps/api/js?key=${GOOGLE_API_KEY}&libraries=places`;
                document.body.appendChild(script);
                script.addEventListener('load', callback);
            }
        };

        loadGoogleMapScript(() => {
            if (placeInputRef.current) {
                const autocomplete = new (window as any).google.maps.places.Autocomplete(placeInputRef.current);
                autocomplete.addListener('place_changed', () => {
                    const place = autocomplete.getPlace();
                    const formatAddress = place.formatted_address;
                    const lat = place.geometry?.location?.lat();
                    const lng = place.geometry?.location?.lng();
                    const address = place.address_components;

                    let items: any = {};
                    if (Array.isArray(address)) {
                        address.forEach((comp: any) => {
                            if (comp.types.includes('postal_code')) items.pin_code = comp.long_name;
                            if (comp.types.includes('administrative_area_level_1')) items.state = comp.long_name;
                            if (comp.types.includes('locality')) items.city = comp.long_name;
                            if (comp.types.includes('street_number')) items.apartment_number = comp.long_name;
                        });
                    }

                    setFormData((prev) => ({
                        ...prev,
                        full_address: formatAddress || '',
                        city: items.city || '',
                        state: items.state || '',
                        pin_code: items.pin_code || '',
                        apartment_number: items.apartment_number || '',
                        latitude: lat?.toString() || '',
                        longitude: lng?.toString() || '',
                    }));

                    setFullAddressErr('');
                    setCityErr('');
                    setStateErr('');
                    setPinCodeErr('');
                    setApartmentNoErr('');
                });
            }
        });
    };

    useEffect(() => {
        initPlaceAPI();
    }, []);

    // Geocode address if lat/lng missing
    const geocodeAddress = async (address: string): Promise<{ lat: string; lng: string } | null> => {
        try {
            const response = await fetch(
                `https://maps.googleapis.com/maps/api/geocode/json?address=${encodeURIComponent(address)}&key=${GOOGLE_API_KEY}`
            );
            const data = await response.json();
            if (data.status === 'OK' && data.results && data.results.length > 0 && !data.results[0].partial_match) {
                const location = data.results[0].geometry.location;
                return { lat: location.lat.toString(), lng: location.lng.toString() };
            }
        } catch (error) {
            console.error('Geocoding error:', error);
        }
        return null;
    };

    const handleAddressGeocode = async (): Promise<{ lat: string; lng: string } | null> => {
        const { full_address, city, state, pin_code } = formData;
        if (city && state) {
            const addressString = [city, state, pin_code, full_address].filter(Boolean).join(', ');
            const coordinates = await geocodeAddress(addressString);
            if (coordinates) {
                setFormData((prev) => ({
                    ...prev,
                    latitude: coordinates.lat,
                    longitude: coordinates.lng,
                }));
                return coordinates;
            }
        }
        return null;
    };

    // Save changes
    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        const { name, phone_number, country_code, full_address, city, state, pin_code, apartment_number } = formData;

        // Validation
        let hasError = false;
        if (!name.trim()) { setNameErr(`${language_data?.[authState?.lang]?.common_name || 'Name'} is required`); hasError = true; }
        if (!phone_number) { setPhoneErr(`${language_data?.[authState?.lang]?.common_phone || 'Phone'} is required`); hasError = true; }
        if (phone_number) {
            const fullNumber = `+${country_code}${phone_number}`;
            try {
                const valid = countryIsoCode
                    ? isValidPhoneNumber(fullNumber, countryIsoCode.toUpperCase() as any)
                    : isValidPhoneNumber(fullNumber);
                if (!valid) { setPhoneErr('Please provide a valid phone number'); hasError = true; }
            } catch {
                setPhoneErr('Please provide a valid phone number');
                hasError = true;
            }
        }
        if (!full_address.trim()) { setFullAddressErr('Full address is required'); hasError = true; }
        if (!city.trim()) { setCityErr('City is required'); hasError = true; }
        if (!state.trim()) { setStateErr('State is required'); hasError = true; }
        if (!pin_code.trim()) { setPinCodeErr('Pin code is required'); hasError = true; }

        if (hasError) return;

        setLoading(true);
        try {
            // Get coordinates if missing
            let { latitude, longitude } = formData;
            if (!latitude || !longitude) {
                const coords = await handleAddressGeocode();
                if (coords) {
                    latitude = coords.lat;
                    longitude = coords.lng;
                }
            }

            // Prepare payload
            const payload: any = {
                name: name.trim(),
                phone_number: Number(phone_number),
                country_code: country_code,
                full_address: full_address.trim(),
                city: city.trim(),
                state: state.trim(),
                pin_code: pin_code.trim(),
                apartment_number: apartment_number?.trim() || '',
                latitude: latitude || '',
                longitude: longitude || '',
            };

            const response = await henceforthApi.Store.updateStore(id!, payload);
            if (response?.data) {
                toast.success('Store profile updated successfully.');
                navigate(`/store/${id}`);
            }
        } catch (error) {
            // handleError(error, 'active', authDispatch);
        } finally {
            setLoading(false);
        }
    };

    const onKeyDown = (e: any) => {
        if ((e.charCode || e.keyCode) === 13) e.preventDefault();
    };

    if (loading && !storeData) {
        return (
            <div className="page-spacing">
                <div className="container-fluid">
                    <div className="row justify-content-center">
                        <div className="col-12 text-center py-5">
                            <div className="spinner-border text-theme" role="status">
                                <span className="visually-hidden">Loading...</span>
                            </div>
                            <p className="mt-3 text-muted">Loading store details...</p>
                        </div>
                    </div>
                </div>
            </div>
        );
    }

    if (!storeData) {
        return (
            <div className="page-spacing">
                <div className="container-fluid text-center py-5">
                    <h4>Store not found</h4>
                    <button className="btn btn-theme mt-3" onClick={() => navigate('/stores')}>
                        Back to Stores
                    </button>
                </div>
            </div>
        );
    }

    const handleDelete = async () => {
        if (!id) return;
        setDeleteLoading(true);
        try {
            await henceforthApi.Store.deleteStore(id);
            toast.success('Store deleted successfully.');
            navigate('/store');
        } catch (error) {
            console.log(error)
            handleError(error, 'active', authDispatch);
        } finally {
            setDeleteLoading(false);
            setShowDeleteModal(false);
        }
    };

    return (
        <Fragment>
            <BreadCrumb pathNameDeclare={breadCrumbPath} />

            <div className="page-spacing">
                <section className="edit-profile">
                    <div className="container-fluid">
                        <div className="row justify-content-center">
                            <div className="col-sm-9 px-0">
                                <div className="common-card h-100">
                                    <div className="common-card-title d-flex justify-content-between align-items-center">
                                        <h5>{language_data?.[authState?.lang]?.common_edit_store || 'Edit Store'}</h5>

                                        <button className='btn btn-danger' onClick={() => setShowDeleteModal(true)}>Delete Store</button>
                                    </div>

                                    <div className="common-card-content">
                                        <form onKeyDown={onKeyDown} onSubmit={handleSubmit}>
                                            {/* Store Name */}
                                            <Inputs.Input
                                                type="text"
                                                id="storeName"
                                                label={`${language_data?.[authState?.lang]?.common_store_name || 'Store Name'}`}
                                                error={nameErr}
                                                value={formData.name}
                                                placeholder="Enter store name"
                                                name="name"
                                                handleChange={changeFormData}
                                            />

                                            {/* Phone */}
                                            <div className={`form-fields-box mb-2 rounded-0 ${phoneErr ? 'border border-danger position-relative' : ''}`} id="phone">
                                                <label className="fw-bolder mb-1">
                                                    {language_data?.[authState?.lang]?.common_phone || 'Phone'} No.
                                                </label>
                                                <PhoneInput
                                                    country={'gm'}
                                                    disableDropdown={true}
                                                    value={`${formData.country_code}${formData.phone_number}`}
                                                    inputProps={{ required: true }}
                                                    onChange={(phone: any, value: any) => phoneCheck({ phone }, value)}
                                                />
                                                {phoneErr ? <span className="svg-question"><Svg /></span> : ''}
                                            </div>
                                            <Errormessage phone={phoneErr} error={phoneErr} />

                                            {/* Location Search */}
                                            {/* <Inputs.InputRef
                                                type="text"
                                                label={`${language_data?.[authState?.lang]?.common_search_location || 'Search Location'}`}
                                                ref={placeInputRef}
                                                handleChange={changeFormData}
                                            /> */}

                                            {/* Full Address */}
                                            <Textarea
                                                error={fullAddressErr}
                                                id="full_address"
                                                label={`${language_data?.[authState?.lang]?.common_full_address || 'Full Address'}`}
                                                col={10}
                                                row={3}
                                                name="full_address"
                                                placeholder="Enter full address"
                                                value={formData.full_address}
                                                handleChange={changeFormData}
                                            />

                                            {/* City */}
                                            <Inputs.Input
                                                type="text"
                                                id="city"
                                                label="City"
                                                error={cityErr}
                                                value={formData.city}
                                                placeholder="Enter city"
                                                name="city"
                                                handleChange={changeFormData}
                                            />

                                            {/* State */}
                                            <Inputs.Input
                                                type="text"
                                                id="state"
                                                label="State"
                                                error={stateErr}
                                                value={formData.state}
                                                placeholder="Enter state"
                                                name="state"
                                                handleChange={changeFormData}
                                            />

                                            {/* Pin Code */}
                                            <Inputs.Input
                                                type="text"
                                                maxLength={6}
                                                id="pin_code"
                                                label="Pin Code"
                                                error={pinCodeErr}
                                                value={formData.pin_code}
                                                placeholder="Enter pin code"
                                                name="pin_code"
                                                handleChange={changeFormData}
                                            />

                                            {/* Apartment Number (optional) */}
                                            <Inputs.Input
                                                type="text"
                                                id="apartment_number"
                                                label="Apartment / Suite Number (optional)"
                                                error={apartmentNoErr}
                                                value={formData.apartment_number}
                                                placeholder="Enter apartment or suite number"
                                                name="apartment_number"
                                                handleChange={changeFormData}
                                            />

                                            <SaveCancelButton loading={loading} color="text-white" />
                                        </form>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </section>
            </div>

            {/* Delete Confirmation Modal */}
            {showDeleteModal && (
                <>
                    <div className="modal-backdrop fade show"></div>
                    <div className="modal fade show" style={{ display: 'block' }} tabIndex={-1} role="dialog">
                        {/* Added modal-dialog-centered class to center vertically and horizontally */}
                        <div className="modal-dialog modal-dialog-centered" role="document">
                            <div className="modal-content">
                                <div className="modal-header">
                                    <h5 className="modal-title">Confirm Delete</h5>
                                    <button type="button" className="btn-close" onClick={() => setShowDeleteModal(false)} aria-label="Close"></button>
                                </div>
                                <div className="modal-body">
                                    <p>Are you sure you want to delete this store? This action cannot be undone.</p>
                                </div>
                                <div className="modal-footer">
                                    <button type="button" className="btn btn-secondary" onClick={() => setShowDeleteModal(false)} disabled={deleteLoading}>
                                        Cancel
                                    </button>
                                    <button type="button" className="btn btn-danger" onClick={handleDelete} disabled={deleteLoading}>
                                        {deleteLoading ? 'Deleting...' : 'Delete'}
                                    </button>
                                </div>
                            </div>
                        </div>
                    </div>
                </>
            )}
        </Fragment>
    );
};

export default EditStoreProfile;